import {
    Box,
    Stack,
    Typography,
    FormControlLabel,
    FormGroup,
    styled,
    Switch,
    SwitchProps,
    Divider,
    CircularProgress,
    Button,
    Chip,
} from "@mui/material";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import {useEffect, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn} from "@lib/apis";
import {useSnackbar} from "notistack";
import { ColDef, ValueFormatterParams } from "ag-grid-community";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import { CustomCellRendererProps } from "ag-grid-react";
import { GenericCsvExportButton, colDefsToCsvColumns } from "@components/ExportToCSV";
import { format } from "date-fns";

interface PageData {
    ns_check_enabled: boolean;
    ns_auto_pause_enabled: boolean;
    ns_interval_check_enabled: boolean;
    ns_auto_resume_enabled: boolean;
}

interface CustomizedSwitchProps {
    checked: boolean;
    onChange: (value: boolean) => void;
}

export default function AdminNSSettings() {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [searchQuery, setSearchQuery] = useState("");
    const [nsCheckEnabled, setNSCheckEnabled] = useState<boolean>(false);    
    const [nsAutoPauseEnabled, setNSAutoPauseEnabled] = useState<boolean>(false);
    const [nsIntervalCheckEnabled, setNSIntervalCheckEnabled] = useState<boolean>(false);
    const [nsAutoResumeEnabled, setNSAutoResumeEnabled] = useState<boolean>(false);

    // FETCH CURRENT NS SETTING
    const pageDataQuery = useQuery({
        queryKey: ["adminNsSetting"],
        queryFn: () => authenticateAndFetchData("/admin/get_ns_setting/"),
        refetchOnWindowFocus: false,
        retry: retryFn,
    });

    const mismatchedDomainsQuery = useQuery({
        queryKey: ["adminMisconfiguredNsDomains", currentPage, pageSize, searchQuery],
        queryFn: () => {
            let url = `/admin/misconfigured-ns-domains/?page=${currentPage}&page_size=${pageSize}`;
            if (searchQuery.trim()) {
                url += `&search=${encodeURIComponent(searchQuery.trim())}`;
            }
            return authenticateAndFetchData(url);
        },
        refetchOnWindowFocus: false,
        retry: retryFn,
    });

    const pageData = mismatchedDomainsQuery.data?.data;

    useEffect(() => {
        if (pageDataQuery.data) {
            const data = pageDataQuery.data.data as PageData;

            setNSCheckEnabled(data.ns_check_enabled);
            setNSAutoPauseEnabled(data.ns_auto_pause_enabled);
            setNSIntervalCheckEnabled(data.ns_interval_check_enabled);
            setNSAutoResumeEnabled(data.ns_auto_resume_enabled)
        }
    }, [pageDataQuery.data]);

    // SAVE NS SETTING
    const nsSettingsMutation = useMutation({
        mutationKey: ["NsSettings"],
        mutationFn: (payload: Partial<PageData>) =>
            authenticateAndPostData("/admin/get_ns_setting/", payload),
        retry: retryFn,
        onSuccess: () => {
            enqueueSnackbar("NS settings updated successfully", {
                variant: "success",
            });
            pageDataQuery.refetch();
        },
        onError: (error: ApiRequestFailed) => {
            enqueueSnackbar(error.data.message, { variant: "error" });
        },
    });

    const adminNsVerifyMutation = useMutation({
        mutationKey: ["adminNsVerify"],
        mutationFn: async ({ id }: { id: number }) => {          
            return authenticateAndPostData("/admin/ns-verify/", { id });
        },
        onSuccess: (response) => {
            enqueueSnackbar("NS records verified successfully.", { variant: "success" });
            // Refresh the mismatched list so the domain disappears if it's now healthy
            queryClient.invalidateQueries({ queryKey: ["mismatchedDomainsQuery"] }); 
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.data?.message || "Verification failed.", { variant: "error" });
        },
    });
    
    const handleNSCheckToggle = (value: boolean) => {
        setNSCheckEnabled(value);
        nsSettingsMutation.mutate({ ns_check_enabled: value });
    };

    const handleAutoPauseToggle = (value: boolean) => {
        setNSAutoPauseEnabled(value);
        nsSettingsMutation.mutate({ ns_auto_pause_enabled: value });
    };

    const handleAutoResumeToggle = (value: boolean) => {
        setNSAutoResumeEnabled(value);
        nsSettingsMutation.mutate({ ns_auto_resume_enabled: value });
    };

    const handleIntervalToggle = (value: boolean) => {
        setNSIntervalCheckEnabled(value);
        nsSettingsMutation.mutate({ ns_interval_check_enabled: value });
    };

    if (pageDataQuery.isLoading) return <PageLoading />;

    if (pageDataQuery.error) {
        return (
            <PageDataErrorHandler
                error={pageDataQuery.error as ApiRequestFailed}
            />
        );
    }

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setCurrentPage(1); // Reset to first page when changing page size
    };

    const handleSearchChange = (search: string) => {
        setSearchQuery(search);
        setCurrentPage(1); // Reset to first page when searching
    };

    const columnCustomizerLabel = {
        domain: "Domain Name",
        workspace_name: "Workspace",
        user_email: "Owner Email",
        ns_mismatch_detected_at: "Failure Date",
        last_known_ns: "Detected Records",
        action: "Run Manual Check"
    };

    const NsStatusRenderer = (cellProps: CustomCellRendererProps) => {
            const status = cellProps.value as string | null;
    
            if (!status) {
                return <span style={{ color: "#999" }}>---</span>;
            }
    
            let label = "";
            let color: "success" | "error" | "warning" | "default" = "default";
    
            if (status === "healthy") {
                label = "Healthy";
                color = "success";
            } else if (status === "mismatch") {
                label = "Mismatch";
                color = "error";
            }
    
            return <Chip label={label} color={color} size="small" />;
        };
    
    function timestampFormatter(params: ValueFormatterParams) {
            if (params.value !== null) {
                return format(new Date(params.value), "do MMM y, HH:mm:ss (xxx)");
            } else {
                return "---"
            }
        }

    const columnDefs : ColDef[] = [
        { field: "domain", headerName: "Domain", minWidth: 200, flex: 1 },
        { field: "workspace_name", headerName: "Workspace", minWidth: 150 },
        { field: "user_email", headerName: "User Email", minWidth: 200 },
        {
            field: "ns_status",
            headerName: "NS Status",
            cellRenderer: NsStatusRenderer,
            minWidth: 140,
        },
        { 
            field: "ns_mismatch_detected_at", 
            headerName: "Detected At", 
            minWidth: 180,
            valueFormatter: timestampFormatter,
            cellRenderer: (params: any) => params.value ? new Date(params.value).toLocaleString() : "N/A"
        },
        // { 
        //     field: "last_known_ns", 
        //     headerName: "Current NS", 
        //     minWidth: 250,
        //     cellRenderer: (params: any) => (
        //         <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
        //             {params.value?.join(", ") || "None detected"}
        //         </Typography>
        //     )
        // },
        {
            field: "action",
            headerName: "Verify",
            width: 150, // Increased width slightly to fit the text
            pinned: 'right',
            cellRenderer: (params: any) => {
                const domainId = params.data.id;
                // Check if THIS specific row is currently being mutated
                const isVerifying = adminNsVerifyMutation.isPending && 
                                adminNsVerifyMutation.variables?.id === domainId;

                return (
                    <Button
                        variant="outlined"
                        size="small"
                        color="primary"
                        disabled={isVerifying}
                        onClick={() => adminNsVerifyMutation.mutate({ id: domainId })}
                        startIcon={isVerifying ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {isVerifying ? "Verifying..." : "NS Verify"}
                    </Button>
                );
            }
        }
    ]

    return (
        <Stack direction={"column"} spacing={2} sx={{ pb: 4, height: "100%" }}>
            <Typography variant="h4">NS Settings</Typography>

            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                maxWidth={500}
            >
                <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Enable NS Record Monitoring
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Periodically verify DNS NS records for all connected domains
                    </Typography>
                </Box>

                <CustomizedSwitch
                    checked={nsCheckEnabled}
                    onChange={handleNSCheckToggle}
                />
            </Box>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                maxWidth={500}
            >
                <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Enable Interval NS Check
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Run NS verification at regular intervals (5min, 15min, 30min and 1hr)
                    </Typography>
                </Box>

                <CustomizedSwitch
                    checked={nsIntervalCheckEnabled}
                    onChange={handleIntervalToggle}
                />
            </Box>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                maxWidth={500}
            >
                <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Enable Auto Pause on NS Record Change
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Automatically pause campaigns if DNS NS records change for any connected domain
                    </Typography>
                </Box>

                <CustomizedSwitch
                    checked={nsAutoPauseEnabled}
                    onChange={handleAutoPauseToggle}
                />
            </Box>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                maxWidth={500}
            >
                <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Enable Auto Resume on NS Record Change
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Automatically resume campaigns if DNS NS records change for any connected domain
                    </Typography>
                </Box>

                <CustomizedSwitch
                    checked={nsAutoResumeEnabled}
                    onChange={handleAutoResumeToggle}
                />
            </Box>
            <Divider />
                <Box sx={{ height: "600px", width: "100%", overflow: "hidden" }}>
                    <PaginatedServerSideDataTable
                        columns={columnDefs}
                        paginationData={pageData}
                        loading={mismatchedDomainsQuery.isLoading || mismatchedDomainsQuery.isFetching}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onSearchChange={handleSearchChange}
                        searchPlaceholder="Search by domain, user email, or workspace..."                        
                        columnLabels={columnCustomizerLabel}
                        actions={<GenericCsvExportButton
                            endpoint="/admin/misconfigured-ns-domains/"
                            queryParams={{
                                search: searchQuery,
                            }}
                            columns={colDefsToCsvColumns(columnDefs)}
                            exportName={"NSDomains"}
                            transformResponse={(res) => res?.data?.data || []}
                        />}
                    />
                </Box>
        </Stack>
    );
}

/* ---------- SWITCH COMPONENT ---------- */

function CustomizedSwitch({ checked, onChange }: CustomizedSwitchProps) {
    const IOSSwitch = styled((props: SwitchProps) => (
        <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
    ))(({ theme }) => ({
        width: 42,
        height: 26,
        padding: 0,
        '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: 2,
            transitionDuration: '300ms',
            '&.Mui-checked': {
                transform: 'translateX(16px)',
                color: '#fff',
                '& + .MuiSwitch-track': {
                    backgroundColor: '#0288d1',
                    opacity: 1,
                },
            },
        },
        '& .MuiSwitch-thumb': {
            width: 22,
            height: 22,
        },
        '& .MuiSwitch-track': {
            borderRadius: 13,
            backgroundColor: '#E9E9EA',
            opacity: 1,
        },
    }));

    return (
        <FormGroup>
            <FormControlLabel
                control={
                    <IOSSwitch
                        checked={checked}
                        onChange={(e) => onChange(e.target.checked)}
                    />
                }
                label=""
            />
        </FormGroup>
    );
}
